import React from "react";
import classNames from "classnames";
import { Link, NavLink } from "react-router-dom";
import { Container } from "../Grid/Grid";
import { Svg, SVG_ICONS } from "../__common/Svg/Svg";
import { Typography } from "../__common/Typography/Typography";
import { Tooltip } from "../__common/Tooltip/Tooltip";
import { HeaderMenu } from "../../constants/nav";

import "./Drawer.scss";
import { useEffect, useState } from "react";
import { Image } from "../__common/Image/Image";
import { LinkButton } from "../__common/_controls";
import { useUserHooks } from "../../hooks/user-hooks";
import { API_ENDPOINTS, API_SA_BASE_URL } from "../../constants/api";
import { LOGIN_ID_TYPE } from "../../types/user";

import { Trans, useTranslation } from "react-i18next";
import triggerGAEvent from "../../utils/UtilsTriggerGAEvent";
import { EVENT_CATEGORY_HEADER } from "../../constants/analytics";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { LayoutState } from "../../store/reducers/layout-reducer";
import { UserState } from "../../store/reducers/user-reducer";
import {
  CountriesWithLocalPrivacy,
  EuropeCountriesExceptTurkey,
} from "../../data/landing-page";
import LocalPrivacySiteModal from "../../pages/nonauth/LandingPage/LocalPrivacySiteModal/LocalPrivacySiteModal";
import { COUNTRY_FLAGS } from "../__common/Image/flagImages";

interface Props {
  isDrawerOpen: boolean;
  isLogin?: boolean;
  onClickDrawerClose?: () => void;
  onClickLogin?: () => void;
  onClickSignOut?: () => void;
}

export const Drawer: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { userData } = useUserHooks();
  const {
    isDrawerOpen,
    onClickDrawerClose,
    isLogin,
    onClickLogin,
    onClickSignOut,
  } = props;
  const [activeTooltip, setActiveTooltip] = useState<string | number>("");
  const [hasLocalSite, setHasLocalSite] = useState<boolean>(false);
  const [localSite, setLocalSite] = useState<string | null>("");
  const wrapClass = classNames("menu-drawer", { open: !!isDrawerOpen });
  const [isCountryListModalOpen, setIsCountryListModalOpen] = useState(false);
  const [isEuropeCountry, setIsEuropeCountry] = useState<boolean>(false);
  const { selectedCountry, isHighContrast } = useSelector<
    ApplicationState,
    LayoutState
  >((state) => state.layout);
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const hasLocalSiteAndNotEuCountry = hasLocalSite && !isEuropeCountry;

  const handleOnClickDrawerClose = () => {
    if (onClickDrawerClose) {
      handleOnClickTooltipClose();
      onClickDrawerClose();
    }
  };

  const handleMenuClick = (gaAction: string) => {
    if (onClickDrawerClose) {
      triggerGAEvent(EVENT_CATEGORY_HEADER, gaAction);
      onClickDrawerClose();
    }
  };

  const handleOnClickTooltip = (id: string | number) => {
    setActiveTooltip(id);
  };

  const handleOnClickTooltipClose = () => {
    setActiveTooltip("");
  };

  const redirectToLocalSite = () => {
    if (localSite) {
      window.open(localSite, "_self");
    }
  };

  useEffect(() => {
    const body = document.body;

    if (isDrawerOpen) {
      body.classList.add("modal-open");
    } else {
      body.classList.remove("modal-open");
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    const countryCode = selectedCountry.code
      ? selectedCountry.code
      : userCountryCode;

    if (isDrawerOpen) {
      handleOnClickDrawerClose();
    }
    const hasCountryLocalSite = CountriesWithLocalPrivacy.some(
      (country) => country.code === countryCode
    );

    const isEuropeCountryExceptTurkey = EuropeCountriesExceptTurkey.some(
      (country) => country.code === countryCode
    );
    setIsEuropeCountry(isEuropeCountryExceptTurkey);

    if (hasCountryLocalSite) {
      const site = CountriesWithLocalPrivacy.filter(
        (country) => country.code === countryCode
      )[0].link;
      setLocalSite(site);
    }

    setHasLocalSite(hasCountryLocalSite);
  }, [selectedCountry, userCountryCode]);

  return (
    <>
      <div className={wrapClass}>
        <div className="menu-drawer__close">
          <div
            className="menu-drawer__close__btn"
            onClick={handleOnClickDrawerClose}
          >
            <Svg icon={SVG_ICONS.DRAWER_CLOSE} width={24} height={24} />
          </div>
        </div>
        <Container>
          <div className="menu-drawer__profile">
            <div className="menu-drawer__profile__icon">
              {isLogin ? (
                userData.profileImageUrl ? (
                  <Image
                    image={userData.profileImageUrl}
                    name={userData.name}
                    width={48}
                    height={48}
                    onClick={onClickLogin}
                  />
                ) : (
                  <Svg
                    icon={SVG_ICONS.PROFILE}
                    className="profile-placeholder"
                    width={48}
                    height={48}
                  />
                )
              ) : (
                <Svg
                  icon={SVG_ICONS.PROFILE_SIGNOUT_CIRCLE}
                  width={48}
                  height={48}
                />
              )}
            </div>
            <div className="menu-drawer__profile__text__container">
              <span onClick={onClickLogin}>
                <Typography
                  className="menu-drawer__profile__text"
                  variant="title3"
                >
                  {isLogin ? userData.name : t("PRIVACY_HEADER_SIGNIN")}
                </Typography>
              </span>
            </div>
            {isLogin && (
              <div onClick={onClickSignOut}>
                <span className="menu-drawer__signout-button">
                  {t("PRIVACY_HEADER_SIGNOUT")}
                </span>
              </div>
            )}
          </div>
          <div
            className={classNames("menu-drawer__message_container", {
              login: !!isLogin,
            })}
          >
            <Typography
              className="menu-drawer__message"
              variant="title1"
              weight="w400"
            >
              {t("PRIVACY_HEADER_USER_POPUP_GREETING")} <br />
              {t("PRIVACY_HEADER_USER_POPUP_GREETING_TEXT")}
            </Typography>
          </div>
          {isLogin && (
            <div className="menu-drawer__email">
              <LinkButton
                title={
                  userData.loginIdType === LOGIN_ID_TYPE.EMAIL
                    ? userData.validatedEmail
                    : userData.loginId
                }
                to={{
                  pathname: `${API_SA_BASE_URL}${API_ENDPOINTS.SA_PROFILE}`,
                }}
                target="_blank"
                underlined={false}
                icon={SVG_ICONS.GREATE_THAN_SML}
                textClassName="menu-drawer__email__text"
              />
            </div>
          )}
          <div className="horizontal-divider" />
          <div className="menu-drawer__navs">
            <ul>
              {HeaderMenu.map((menu) => (
                <li className="menu-drawer__navs__item" key={menu.id}>
                  <div className="nav-item">
                    <div className="item-icon">
                      {menu.icon && (
                        <Svg icon={menu.icon} width={22.9} height={18.4} />
                      )}
                    </div>
                    <div className="item-text">
                      {menu.title.link !== "" ? (
                        menu.id === 2 && hasLocalSiteAndNotEuCountry ? (
                          <Typography
                            onClick={redirectToLocalSite}
                            variant="title2"
                          >
                            {t(menu.title.text)}
                          </Typography>
                        ) : (
                          <Link
                            to={menu.title.link}
                            onClick={() => handleMenuClick(menu.gaAction)}
                          >
                            {t(menu.title.text)}
                          </Link>
                        )
                      ) : (
                        <Typography variant="title2">
                          {t(menu.title.text)}
                        </Typography>
                      )}
                    </div>
                    {menu.items && (
                      <div
                        className="item-tooltip"
                        onClick={() => handleOnClickTooltip(menu.id)}
                      >
                        <Svg
                          icon={SVG_ICONS.TOOLTIP_OUTLINE}
                          width={24}
                          height={24}
                        />
                      </div>
                    )}
                  </div>
                  {activeTooltip === menu.id && menu.items && (
                    <Tooltip
                      className="menu-item-tooltip"
                      onClickClose={handleOnClickTooltipClose}
                    >
                      {menu.items.map((item) => (
                        <>
                          <Typography className="tooltip-title" variant="body3">
                            {t(item.text)}
                          </Typography>
                          <Typography
                            className="tooltip-sub-title"
                            variant="body3"
                          >
                            {item.detail ? t(item.detail.text) : ""}
                          </Typography>
                        </>
                      ))}
                    </Tooltip>
                  )}
                  <ul className="sub-items">
                    {menu.showLocation && hasLocalSiteAndNotEuCountry ? (
                      <>
                        <li className="sub-item-link">
                          <div>
                            <Typography
                              variant="title3"
                              weight="w400"
                              onClick={redirectToLocalSite}
                            >
                              {t("PRIVACY_HUB_MOBILE_DRAWER_MANAGEDATA")}
                            </Typography>
                          </div>
                          <div className="sub-item-location">
                            {isHighContrast ? (
                              <Svg
                                icon={SVG_ICONS.MYDATA_LOCATION_ARROW_HC}
                                width={12}
                                height={12}
                              />
                            ) : (
                              <Svg
                                icon={SVG_ICONS.MYDATA_LOCATION_ARROW}
                                width={12}
                                height={12}
                              />
                            )}
                            <div>
                              <Typography
                                variant="title3"
                                weight="w400"
                                onClick={redirectToLocalSite}
                              >
                                <Trans
                                  i18nKey="PRIVACY_HUB_MOBILE_DRAWER_MYLOCATION"
                                  components={{
                                    span: <span></span>,
                                  }}
                                  values={{ "%s": selectedCountry.name }}
                                />
                                &nbsp;
                              </Typography>
                            </div>
                            <Image
                              image={COUNTRY_FLAGS[selectedCountry.code]}
                              width={34}
                              height={18}
                            />
                          </div>
                        </li>
                        <li className="sub-item-link">
                          <Typography
                            variant="title3"
                            weight="w400"
                            onClick={() => {
                              setIsCountryListModalOpen(true);
                            }}
                          >
                            {t("PRIVACY_HUB_MOBILE_DRAWER_CHANGE_COUNTRY")}
                          </Typography>
                        </li>
                      </>
                    ) : null}
                    {menu.items &&
                      menu.items.map((item) => (
                        <li className="sub-item-link" key={item.id}>
                          <NavLink
                            to={item.link}
                            onClick={() => handleMenuClick(item.gaAction)}
                          >
                            {t(item.text)}
                          </NavLink>
                        </li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </Container>
        <LocalPrivacySiteModal
          show={isCountryListModalOpen}
          onClickClose={() => {
            setIsCountryListModalOpen(false);
          }}
        />
      </div>
    </>
  );
};
